// Groups an asset's contracts by contract type (« poste » in the French UI):
// the asset's Contracts tab shows one collapsible group per type. Not the
// legal family of the type tree (contract-type-tree.ts) nor a clause family.

/** Group code of the contracts that carry no contract type. */
export const NO_CONTRACT_TYPE_GROUP = 'none'

interface TypeLike {
  code?: string
  names?: unknown
}

interface ContractLike {
  contract_type?: TypeLike | null
}

export interface ContractTypeGroupRows<C extends ContractLike> {
  /** The expected code the contracts matched, their own type code, or `none`. */
  typeCode: string
  /** The type the group is named after; undefined for `none` or an unknown code. */
  contractType?: TypeLike
  contracts: C[]
}

export interface ContractTypeGroupsOptions {
  /** Active UI language, for the group order. */
  locale?: string
  /** Every contract type of the organization, to name a group keyed on an expected code. */
  types?: TypeLike[]
}

/** The type's name in the active locale (locale → en → fr → first → code). */
export function contractTypeName(type: TypeLike | undefined, locale = 'en'): string {
  const names = type?.names as Record<string, string> | string | undefined
  if (typeof names === 'string' && names) return names
  if (names && typeof names === 'object') {
    const n = names[locale] || names.en || names.fr || Object.values(names)[0]
    if (n) return n
  }
  return type?.code ?? ''
}

/** True when code is expected or one of its sub-types (`<expected>_…`), the TypeCodesSQL rule. */
function matches(code: string, expected: string): boolean {
  return code === expected || code.startsWith(`${expected}_`)
}

/**
 * Groups contracts by type. A contract goes under the longest expected code it
 * matches (equal, or prefix `<code>_`), else under its own type code; one with
 * no type goes to `none`. Groups are ordered by localized type name, `none`
 * last; contracts keep their input order.
 */
export function contractTypeGroups<C extends ContractLike>(
  contracts: C[],
  expectedCodes: string[] = [],
  options: ContractTypeGroupsOptions = {},
): ContractTypeGroupRows<C>[] {
  const { locale = 'en', types = [] } = options
  const byExpectedLength = [...expectedCodes].sort((a, b) => b.length - a.length)
  const groups = new Map<string, ContractTypeGroupRows<C>>()
  for (const contract of contracts) {
    const own = contract.contract_type?.code
    const typeCode = own
      ? (byExpectedLength.find((e) => matches(own, e)) ?? own)
      : NO_CONTRACT_TYPE_GROUP
    let group = groups.get(typeCode)
    if (!group) {
      const contractType = own === typeCode
        ? (contract.contract_type ?? undefined)
        : types.find((t) => t.code === typeCode)
      group = { typeCode, contractType, contracts: [] }
      groups.set(typeCode, group)
    }
    group.contracts.push(contract)
  }
  const label = (g: ContractTypeGroupRows<C>) => contractTypeName(g.contractType, locale) || g.typeCode
  return [...groups.values()].sort((a, b) => {
    if (a.typeCode === NO_CONTRACT_TYPE_GROUP) return 1
    if (b.typeCode === NO_CONTRACT_TYPE_GROUP) return -1
    return label(a).localeCompare(label(b), locale)
  })
}
